import { useEffect, useRef, useState } from 'react';

import { apiClient, apiUrl, provisioningPath } from '@/lib/api-client';
import { isTerminalStatus, type ProvisioningTaskStatus, type TaskLogLine } from '@/types/provisioning';

interface TaskLogState {
  lines: TaskLogLine[];
  /** True while the SSE stream / poll loop is connected. */
  streaming: boolean;
  error: string | null;
}

/**
 * Live-tails a provisioning task's output log.
 *
 * The backend exposes `GET /tasks/:id/log?follow=true` as Server-Sent Events.
 * `EventSource` cannot send an `Authorization` header, so when a JWT is in
 * play (production) the SSE path only works if the edge carries the CF Access
 * cookie. To stay correct in every environment — and because MSW cannot mock
 * SSE — this hook polls the non-follow JSON form of the same endpoint every
 * 2s while the task is non-terminal. Once the task settles it fetches the
 * full log one last time and stops. Same data, no header problem.
 */
export function useTaskLog(
  taskId: number | undefined,
  status: ProvisioningTaskStatus | undefined,
): TaskLogState {
  const [lines, setLines] = useState<TaskLogLine[]>([]);
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const finalFetchedRef = useRef(false);

  useEffect(() => {
    finalFetchedRef.current = false;
    setLines([]);
    setError(null);
  }, [taskId]);

  useEffect(() => {
    if (!taskId) {
      setStreaming(false);
      return undefined;
    }

    let cancelled = false;
    const terminal = isTerminalStatus(status);

    async function pull(): Promise<void> {
      try {
        const path = provisioningPath(`/tasks/${taskId}/log`);
        const data = await apiClient.get<TaskLogLine[]>(path);

        if (!cancelled) {
          setLines(data);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load task log');
        }
      }
    }

    if (terminal) {
      // Settled — one final pull, then stop.
      setStreaming(false);

      if (!finalFetchedRef.current) {
        finalFetchedRef.current = true;
        void pull();
      }

      return undefined;
    }

    // Non-terminal — poll the log every 2s (the "live tail").
    setStreaming(true);
    void pull();

    const id = setInterval(() => void pull(), 2_000);

    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [taskId, status]);

  return { lines, streaming, error };
}

/** SSE endpoint URL — exported for callers that can use a native EventSource. */
export function taskLogStreamUrl(taskId: number): string {
  return apiUrl(provisioningPath(`/tasks/${taskId}/log`, { follow: true }));
}
